/**
 * Cart drawer open reliability.
 *
 * cart-drawer.js is a separate module. Clicks and cart:update can ask the
 * drawer to open before cart-drawer-component is defined and upgraded.
 * Those open() / showDialog calls are queued and replayed once the element
 * is connected and its dialog exists.
 *
 * If the drawer then shows the empty state while /cart.js still has items,
 * re-render through Horizon's sectionRenderer (same path cart-items uses).
 */
if (!window.__thsCartDrawerReliability) {
  window.__thsCartDrawerReliability = true;

  const pending = [];
  let flushing = false;
  let syncing = null;
  let rendererPromise = null;

  function cartJsUrl() {
    const routes = window.Theme && Theme.routes;
    if (routes && routes.cart_url) {
      return String(routes.cart_url).endsWith('.js') ? routes.cart_url : `${routes.cart_url}.js`;
    }
    const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    return `${String(root).replace(/\/?$/, '/')}cart.js`;
  }

  function dialogOf(drawer) {
    if (!drawer) return null;
    return drawer.refs?.dialog || drawer.querySelector('dialog');
  }

  function isReady(drawer) {
    const Ctor = customElements.get('cart-drawer-component');
    if (!Ctor || !drawer || !(drawer instanceof Ctor) || !drawer.isConnected) return false;
    return Boolean(dialogOf(drawer));
  }

  function drawerLooksEmpty(drawer) {
    const dialog = dialogOf(drawer);
    if (!dialog) return false;
    if (dialog.classList.contains('cart-drawer--empty')) return true;
    if (dialog.querySelector('.cart-drawer__heading--empty, #cart-drawer-heading-empty')) return true;
    const hasLines = dialog.querySelector('.cart-items__table-row, .cart-item, [data-cart-item]');
    const hasSummary = dialog.querySelector('.cart-drawer__summary');
    return !hasLines && !hasSummary;
  }

  function visibleItemCount() {
    let max = 0;
    document.querySelectorAll('.cart-bubble__text-count, [data-cart-count]').forEach((el) => {
      const n = parseInt(String(el.textContent || '').replace(/[^\d]/g, ''), 10);
      if (Number.isFinite(n)) max = Math.max(max, n);
    });
    return max;
  }

  async function cartItemCount() {
    const visible = visibleItemCount();
    if (visible > 0) return visible;
    try {
      const response = await fetch(cartJsUrl(), {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return 0;
      const cart = await response.json();
      return Number(cart.item_count) || 0;
    } catch {
      return 0;
    }
  }

  function sectionIdFor(drawer) {
    const items = drawer.querySelector('cart-items-component');
    const fromItems = items && items.dataset && items.dataset.sectionId;
    if (fromItems) return fromItems;
    const section = drawer.closest('.shopify-section, [id^="shopify-section-"]');
    if (!section || !section.id) return '';
    return section.id.replace(/^shopify-section-/, '');
  }

  function loadRenderer() {
    if (!rendererPromise) {
      rendererPromise = import('@theme/section-renderer').catch((error) => {
        console.warn('[ths-cart-drawer] section renderer unavailable', error);
        return null;
      });
    }
    return rendererPromise;
  }

  async function refreshDrawer(drawer) {
    const sectionId = sectionIdFor(drawer);
    if (!sectionId) return;
    const mod = await loadRenderer();
    if (mod && mod.sectionRenderer && typeof mod.sectionRenderer.renderSection === 'function') {
      await mod.sectionRenderer.renderSection(sectionId, { cache: false });
      return;
    }
    const url = new URL(window.location.href);
    url.searchParams.set('section_id', sectionId);
    const response = await fetch(url.toString(), { credentials: 'same-origin' });
    if (!response.ok) throw new Error(`section render ${response.status}`);
    const html = await response.text();
    if (mod && typeof mod.morphSection === 'function') {
      await mod.morphSection(sectionId, html);
      return;
    }
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const live = document.querySelector('cart-drawer-component') || drawer;
    const freshInner = parsed.querySelector('.cart-drawer__inner');
    const liveInner = live.querySelector('.cart-drawer__inner');
    if (freshInner && liveInner) liveInner.replaceWith(document.importNode(freshInner, true));
    const freshDialog = parsed.querySelector('dialog.cart-drawer__dialog');
    const liveDialog = dialogOf(live);
    if (freshDialog && liveDialog) liveDialog.className = freshDialog.className;
  }

  function syncDrawerContents(drawer) {
    if (syncing) return syncing;
    syncing = (async () => {
      const current = drawer?.isConnected ? drawer : document.querySelector('cart-drawer-component');
      if (!current || !drawerLooksEmpty(current)) return;
      const count = await cartItemCount();
      const latest = document.querySelector('cart-drawer-component') || current;
      if (count <= 0 || !drawerLooksEmpty(latest)) return;
      const wasOpen = Boolean(dialogOf(latest)?.open);
      try {
        await refreshDrawer(latest);
      } catch (error) {
        console.warn('[ths-cart-drawer] remorph failed', error);
        return;
      }
      const next = document.querySelector('cart-drawer-component');
      const nextDialog = next && dialogOf(next);
      if (wasOpen && next && nextDialog && !nextDialog.open && isReady(next)) {
        const opener = typeof next.showDialog === 'function' ? next.showDialog : next.open;
        if (typeof opener === 'function') {
          next.__thsSkipContentsCheck = true;
          try {
            opener.call(next);
          } finally {
            next.__thsSkipContentsCheck = false;
          }
        }
      }
    })().finally(() => {
      syncing = null;
    });
    return syncing;
  }

  let checkQueued = false;
  function scheduleContentsCheck(drawer) {
    if (checkQueued) return;
    checkQueued = true;
    queueMicrotask(() => {
      checkQueued = false;
      syncDrawerContents(drawer);
    });
  }

  function forceOpen(node) {
    const dialog = dialogOf(node);
    if (dialog && !dialog.open && typeof dialog.showModal === 'function') {
      dialog.showModal();
    }
    if (node) scheduleContentsCheck(node);
  }

  function replay(node, method, args) {
    const fn = node && node[method];
    if (typeof fn === 'function') return fn.apply(node, args);
    if (node && typeof node.open === 'function') return node.open();
    if (node && typeof node.showDialog === 'function') return node.showDialog();
    const dialog = dialogOf(node);
    if (dialog && !dialog.open && typeof dialog.showModal === 'function') dialog.showModal();
    return undefined;
  }

  function flushSoon() {
    if (flushing) return;
    flushing = true;
    customElements.whenDefined('cart-drawer-component').then(() => {
      requestAnimationFrame(() => {
        flushing = false;
        const jobs = pending.splice(0, pending.length);
        jobs.forEach((job) => {
          const node = job.el && job.el.isConnected ? job.el : document.querySelector('cart-drawer-component');
          if (!node) return;
          if (!isReady(node)) {
            job.tries = (job.tries || 0) + 1;
            if (job.tries < 30) pending.push(job);
            else forceOpen(node);
            return;
          }
          replay(node, job.method, job.args);
        });
        if (pending.length) flushSoon();
      });
    });
  }

  function queueOpen(el, method, args) {
    const exists = pending.some((job) => job.el === el && job.method === method);
    if (exists) return;
    pending.push({ el, method, args: args || [], tries: 0 });
    flushSoon();
  }

  function patchCtor(Ctor) {
    const proto = Ctor && Ctor.prototype;
    if (!proto || proto.__thsDrawerReliability) return;
    proto.__thsDrawerReliability = true;

    const origOpen = proto.open;
    const origShow = proto.showDialog;

    if (typeof origOpen === 'function') {
      proto.open = function thsOpen(...args) {
        if (!isReady(this)) {
          queueOpen(this, 'open', args);
          return undefined;
        }
        const result = origOpen.apply(this, args);
        if (!this.__thsSkipContentsCheck) scheduleContentsCheck(this);
        return result;
      };
    }

    if (typeof origShow === 'function') {
      proto.showDialog = function thsShowDialog(...args) {
        if (!isReady(this)) {
          queueOpen(this, 'showDialog', args);
          return undefined;
        }
        const result = origShow.apply(this, args);
        if (!this.__thsSkipContentsCheck) scheduleContentsCheck(this);
        return result;
      };
    }
  }

  function installPatch() {
    const Ctor = customElements.get('cart-drawer-component');
    if (Ctor) patchCtor(Ctor);
  }

  if (!customElements.get('cart-drawer-component')) {
    const originalDefine = customElements.define.bind(customElements);
    customElements.define = function thsDefineCartDrawer(name, ctor, options) {
      if (name === 'cart-drawer-component') patchCtor(ctor);
      return originalDefine(name, ctor, options);
    };
  } else {
    installPatch();
  }

  customElements.whenDefined('cart-drawer-component').then(() => {
    installPatch();
    document.querySelectorAll('cart-drawer-component').forEach((el) => customElements.upgrade(el));
    flushSoon();
  });

  document.addEventListener(
    'click',
    (event) => {
      const trigger = event.target instanceof Element
        ? event.target.closest(
            'cart-drawer-component [on\\:click="/open"], cart-drawer-component [data-testid="cart-drawer-trigger"]'
          )
        : null;
      if (!trigger) return;
      const drawer = trigger.closest('cart-drawer-component');
      if (!drawer || isReady(drawer)) return;
      event.preventDefault();
      event.stopPropagation();
      queueOpen(drawer, 'open', []);
    },
    true
  );

  document.addEventListener(
    'cart:update',
    (event) => {
      const data = event.detail?.data || {};
      if (data.didError) return;
      document.querySelectorAll('cart-drawer-component[auto-open]').forEach((drawer) => {
        if (!isReady(drawer)) queueOpen(drawer, 'showDialog', []);
      });
    },
    true
  );
}
